/**
 * Calor Document Cache
 *
 * Stores Calor portal PDFs in IndexedDB so they remain viewable offline and
 * survive transient "failed to connect to server" errors.
 *
 * Each entry is keyed by filename and stores the raw PDF bytes alongside the
 * server-provided version string. New versions are "pushed" from the backend
 * simply by replacing the PDF file: its version string changes, the client
 * detects the mismatch on next online load, and the cache is refreshed
 * automatically — no user refresh/clear required.
 */

const DB_NAME = 'CalorDocCache';
const DB_VERSION = 1;
const STORE_NAME = 'documents';

const openDatabase = () =>
  new Promise((resolve, reject) => {
    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(err);
      return;
    }

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'filename' });
      }
    };
  });

/**
 * Load a cached document.
 * @param {string} filename
 * @returns {Promise<{filename:string, version:string, data:ArrayBuffer, cachedAt:number}|null>}
 */
export const getCachedDoc = async (filename) => {
  try {
    const db = await openDatabase();
    return await new Promise((resolve) => {
      const tx = db.transaction([STORE_NAME], 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(filename);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch (err) {
    console.warn('[calorDocCache] read failed:', err?.message || err);
    return null;
  }
};

/**
 * Save (or overwrite) a cached document.
 * @param {string} filename
 * @param {string} version
 * @param {ArrayBuffer} data
 * @returns {Promise<boolean>}
 */
export const saveCachedDoc = async (filename, version, data) => {
  try {
    const db = await openDatabase();
    return await new Promise((resolve) => {
      const tx = db.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put({ filename, version, data, cachedAt: Date.now() });
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    });
  } catch (err) {
    console.warn('[calorDocCache] write failed:', err?.message || err);
    return false;
  }
};

/**
 * Remove a single cached document.
 * @param {string} filename
 * @returns {Promise<boolean>}
 */
export const deleteCachedDoc = async (filename) => {
  try {
    const db = await openDatabase();
    return await new Promise((resolve) => {
      const tx = db.transaction([STORE_NAME], 'readwrite');
      tx.objectStore(STORE_NAME).delete(filename);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch (err) {
    console.warn('[calorDocCache] delete failed:', err?.message || err);
    return false;
  }
};
